import type { ScriptShot } from '@/api/capture';
import { type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

/**
 * The help layer's context (SPEC_CAPTURE_UX_R2 §7): the Shell provides it on every screen; the persistent help button
 * opens the sheet; the screen that is showing registers what "this step" is (`useHelpStep`) so the sheet can say why
 * this shot, offer "Skip this" on an optional shot, and name the step on a help-request. No provider (a component
 * rendered bare in a test) ⇒ every hook is a no-op.
 */
export type HelpScreen = 'door' | 'language' | 'consent' | 'capture' | 'review' | 'receipt' | 'blocked' | 'other';
export type HelpFocus = 'step' | 'recovery';

export interface HelpStep {
  readonly screen: HelpScreen;
  /** The shot on a capture screen — its prompt key drives "why this shot" and the step token on a help-request. */
  readonly shot?: ScriptShot | null;
  /** True only when the template marks the shot optional: the only case that offers "Skip this". */
  readonly optional?: boolean;
  /** What "Skip this" does (the screen's own skip, with the picked reason). */
  readonly onSkip?: ((reason: string) => void) | null;
  readonly tenantName?: string;
  /** What is blocked on this screen, if anything — the recovery card opens on it. */
  readonly blocked?: 'camera' | 'location' | null;
}

interface HelpValue {
  readonly open: boolean;
  readonly focus: HelpFocus;
  readonly step: HelpStep | null;
  readonly show: (focus?: HelpFocus) => void;
  readonly hide: () => void;
  readonly setStep: (s: HelpStep | null) => void;
}

const HelpCtx = createContext<HelpValue | null>(null);

export function HelpProvider({ children }: { children: ReactNode }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState<HelpFocus>('step');
  const [step, setStep] = useState<HelpStep | null>(null);
  const show = useCallback((f: HelpFocus = 'step') => {
    setFocus(f);
    setOpen(true);
  }, []);
  const hide = useCallback(() => setOpen(false), []);
  const value = useMemo(() => ({ open, focus, step, show, hide, setStep }), [open, focus, step, show, hide]);
  return <HelpCtx.Provider value={value}>{children}</HelpCtx.Provider>;
}

const NOOP: HelpValue = { open: false, focus: 'step', step: null, show: () => undefined, hide: () => undefined, setStep: () => undefined };

export function useHelp(): HelpValue {
  return useContext(HelpCtx) ?? NOOP;
}

/** The same registration as an element — for screens that are plain JSX branches rather than components. */
export function HelpStepMarker(step: HelpStep): null {
  useHelpStep(step);
  return null;
}

/** Register what "this step" is while the screen is mounted; cleared on unmount. Safe without a provider. */
export function useHelpStep(step: HelpStep | null): void {
  const ctx = useContext(HelpCtx);
  const setStep = ctx?.setStep;
  const screen = step?.screen;
  const shotId = step?.shot?.shotId;
  const optional = step?.optional;
  const blocked = step?.blocked;
  const tenantName = step?.tenantName;
  const onSkip = step?.onSkip;
  const shot = step?.shot;
  // The callback is read through a ref so a screen's inline `onSkip` (a new function every render) never re-registers.
  const skipRef = useRef(onSkip);
  skipRef.current = onSkip;
  const shotRef = useRef(shot);
  shotRef.current = shot;
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-register on the facts that change what the sheet says (screen, shot id, optional, blocked, tenant), never on object identity
  useEffect(() => {
    if (!setStep) return undefined;
    setStep(
      screen
        ? {
            screen,
            shot: shotRef.current ?? null,
            optional: optional === true,
            onSkip: skipRef.current ? (reason: string) => skipRef.current?.(reason) : null,
            tenantName: tenantName ?? '',
            blocked: blocked ?? null,
          }
        : null,
    );
    return () => setStep(null);
  }, [setStep, screen, shotId, optional, blocked, tenantName]);
}
